import React, { FC, memo, useContext, useCallback, useEffect, useRef, useState } from 'react'

import { useMutation } from '@apollo/client'
import { pick } from 'lodash-es'
import { FormProvider, useForm } from 'react-hook-form'
import { useWizardStep } from 'react-wizard-primitive'

import { SIGN_UP_CUSTOMER, LOGOUT_CUSTOMER } from 'Const'
import { SignUpStates, useCustomer, useSignUpState } from 'Hooks/checkout'
import useEvent from 'Hooks/useEvent'
import { Customer } from 'Types'
import { sendGTMAccountCreatedEvent } from 'Utils/analytics/gtmEvents'
import { noty } from 'Utils/index'

import { StepContext } from '..'
import NextStepBtn from '../NextStepBtn'

import Code from './Code'
import Details from './Details'
import LoggedCustomerView from './LoggedCustomerView'
import Password from './Password'
import Phone from './Phone'
import './index.scss'

export interface FormState {
  code?: string
  email: string
  firstName: string
  lastName: string
  password?: string
  phone: string
  subscribed?: boolean
}

const DEFAULT_FORM_VALUES: FormState = {
  code: '',
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  phone: '',
  subscribed: true,
}

const CustomerFieldsGroup: FC = () => {
  const OTP_LENGTH = 6
  const RESEND_COOLDOWN_SECONDS = 60
  const { checkouting, nextStep } = useContext(StepContext)
  const { isActive, resetToStep } = useWizardStep()
  const customer = useCustomer()
  const [state, setState] = useSignUpState()
  const form = useForm<FormState>({
    defaultValues: DEFAULT_FORM_VALUES,
  })
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0)
  const [statusMessage, setStatusMessage] = useState('')
  const wasCodeStepRef = useRef(false)
  const codeInputRef = useRef<HTMLInputElement | null>(null)

  const { clearErrors, handleSubmit, reset, getValues, setError, setValue } = form

  const [signUp, { loading: signUpLoading }] = useMutation(SIGN_UP_CUSTOMER, {
    update(cache, { data }) {
      cache.modify({
        fields: {
          currentCustomer() {
            if (data.signUpCustomer.customer) {
              return {
                ...(data.signUpCustomer.customer as Customer),
                __typename: 'Customer',
              }
            }
          },
        },
      })
    },
    onCompleted: (data) => {
      if (data.signUpCustomer.state == SignUpStates.COMPLETED) {
        const customer = data.signUpCustomer.customer
        nextStep({ customer: pick(customer, ['firstName', 'lastName', 'email', 'phone']) })
        sendGTMAccountCreatedEvent(customer)
      } else if (data.signUpCustomer.state == SignUpStates.CODE) {
        noty('Enter code from SMS', 'success')
      }
      setState(data.signUpCustomer.state)
    },
  })
  const [logout] = useMutation(LOGOUT_CUSTOMER, {
    update(cache) {
      cache.modify({
        fields: {
          currentCustomer() {
            return null
          },
        },
      })
    },
    onCompleted: () => {
      resetToStep()
      setState(SignUpStates.PHONE)
      setResendSecondsLeft(0)
      setStatusMessage('')
      reset(DEFAULT_FORM_VALUES)
    },
  })

  const handleLogout = useCallback(() => logout(), [logout])
  const startResendCooldown = useCallback(() => setResendSecondsLeft(RESEND_COOLDOWN_SECONDS), [])

  useEffect(() => {
    if (resendSecondsLeft <= 0) return
    const timerId = setInterval(() => {
      setResendSecondsLeft((seconds) => (seconds > 0 ? seconds - 1 : 0))
    }, 1000)

    return () => clearInterval(timerId)
  }, [resendSecondsLeft])

  useEffect(() => {
    const justEnteredCodeStep = state.isCode && !wasCodeStepRef.current
    if (justEnteredCodeStep) {
      startResendCooldown()
      setStatusMessage('Verification code sent. Enter the 6-digit code from SMS.')
      window.requestAnimationFrame(() => {
        codeInputRef.current?.focus()
      })
    }
    wasCodeStepRef.current = state.isCode
  }, [state.isCode, startResendCooldown])

  const onSubmit = handleSubmit(
    (input: FormState) => {
      if (state.isCode && (input.code || '').trim().length !== OTP_LENGTH) {
        setStatusMessage('Enter the 6-digit verification code to continue checkout.')
        setError('code', { message: 'Enter the 6-digit code', type: 'manual' })
        codeInputRef.current?.focus()
        return
      }

      if (customer?.signedUp) {
        nextStep({ customer: pick(customer, ['firstName', 'lastName', 'email', 'phone']) })
        return
      }
      void signUp({ variables: { input } })
    },
    (errors) => {
      if (errors.code) {
        setStatusMessage('Enter the 6-digit verification code to continue checkout.')
        codeInputRef.current?.focus()
      }
    },
  )

  const handleResend = useEvent(() => {
    if (resendSecondsLeft > 0) return
    startResendCooldown()
    setStatusMessage('Verification code resent. Enter the 6-digit code from SMS.')
    clearErrors('code')
    setValue('code', '')
    codeInputRef.current?.focus()
    void signUp({ variables: { input: { phone: getValues('phone') } } })
  })

  const activeSection = state.isCode ? 'code' : state.isForm ? 'details' : state.isPassword ? 'password' : 'phone'
  const isPhoneVerified = state.isForm || state.isPassword || !!customer?.signedUp
  let activeSectionTitle: string | null = null
  let activeSectionDescription: string | null = null
  const phoneSectionDescription = isPhoneVerified
    ? 'Phone number is successfully verified'
    : state.isCode
    ? 'We have sent a 6-digit authorization code to your number'
    : 'We use this number to send a verification code for checkout'

  if (state.isForm) {
    activeSectionTitle = 'Contact Details'
    activeSectionDescription = 'Add your contact details to continue checkout'
  } else if (state.isPassword) {
    activeSectionTitle = 'Welcome Back'
    activeSectionDescription = 'Sign in to continue with your saved customer profile'
  }

  return (
    <FormProvider {...form}>
      <div className="checkout-step / mb-5">
        <h2 className="checkout-step__header / h4">
          <span>Contact</span>

          {isActive ? (
            state.isPhone && !customer?.signedUp ? null : (
              <button className="checkout-button / ml-auto" type="button" onClick={handleLogout}>
                Log out
              </button>
            )
          ) : (
            <button className="checkout-button / ml-auto" type="button" onClick={resetToStep}>
              Change
            </button>
          )}
        </h2>

        <form className="checkout-contact-form" hidden={!isActive || customer?.signedUp} onSubmit={onSubmit}>
          <p aria-live="polite" className="sr-only" role="status">
            {statusMessage}
          </p>

          <div className="checkout-contact-flow">
            {customer?.signedUp ? null : (
              <section className="checkout-contact-flow__panel checkout-contact-flow__panel--phone">
                <div className="checkout-contact-flow__title-row">
                  <h3 className="checkout-contact-flow__title">Verify Phone</h3>
                  {isPhoneVerified ? (
                    <span aria-label="Phone verified" className="checkout-contact-flow__verified-badge" role="img">
                      <svg
                        aria-hidden="true"
                        className="checkout-contact-flow__verified-icon"
                        fill="none"
                        viewBox="0 0 20 20"
                      >
                        <g className="checkout-contact-flow__verified-circle-group">
                          <circle cx="10" cy="10" r="8.5" />
                        </g>
                        <g className="checkout-contact-flow__verified-check-group">
                          <path d="M6.2 10.4 8.8 13 13.8 8" />
                        </g>
                      </svg>
                    </span>
                  ) : null}
                </div>
                <p className="checkout-contact-flow__description mb-1">{phoneSectionDescription}</p>
                <Phone disabled={!state.isPhone} submitting={signUpLoading || checkouting} />
                <div aria-hidden={!state.isCode} className="checkout-contact-flow__code-reveal" hidden={!state.isCode}>
                  {state.isCode ? (
                    <Code
                      canAutoSubmit={!(signUpLoading || checkouting)}
                      codeInputRef={codeInputRef}
                      handleResend={handleResend}
                      handleSubmitCode={onSubmit}
                      resendSecondsLeft={resendSecondsLeft}
                    />
                  ) : null}
                </div>
              </section>
            )}

            {state.isForm || state.isPassword ? (
              <section
                key={activeSection}
                className="checkout-contact-flow__panel checkout-contact-flow__panel--active"
                data-step={activeSection}
              >
                {activeSectionTitle ? <h3 className="checkout-contact-flow__title">{activeSectionTitle}</h3> : null}
                {activeSectionDescription ? (
                  <p className="checkout-contact-flow__description mb-2">{activeSectionDescription}</p>
                ) : null}

                {state.isForm ? <Details /> : state.isPassword ? <Password /> : null}
              </section>
            ) : null}
          </div>
        </form>
        {customer?.signedUp ? (
          <LoggedCustomerView email={customer.email} firstName={customer.firstName} lastName={customer.lastName} />
        ) : null}
      </div>

      {isActive ? <NextStepBtn loading={signUpLoading || checkouting} onSubmit={onSubmit} /> : null}
    </FormProvider>
  )
}

export default memo(CustomerFieldsGroup)
